/*
 * This is a fork of original component:
 * <backstage>/plugins/org/src/components/Cards/Group/MembersList/MembersListCard.tsx
 *
 * As of Backstage 1.43.3 it is impossible to directly override the single member
 * card component, but we need to display extra info there, thus the fork.
 */

import {
  DEFAULT_NAMESPACE,
  GroupEntity,
  UserEntity,
  stringifyEntityRef
} from '@backstage/catalog-model'
import { catalogApiRef, useEntity, EntityRefLink } from '@backstage/plugin-catalog-react'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import Pagination from '@material-ui/lab/Pagination'
import { useState, ChangeEvent } from 'react'
import useAsync from 'react-use/esm/useAsync'
import FlareIcon from '@material-ui/icons/Flare'

import {
  Avatar,
  InfoCard,
  Progress,
  ResponseErrorPanel,
  Link,
  OverflowTooltip
} from '@backstage/core-components'
import { useApi } from '@backstage/core-plugin-api'
import {
  getAllDesendantMembersForGroupEntity,
  removeDuplicateEntitiesFrom
} from './helpers'
import { EntityRelationAggregation } from '@backstage/plugin-org'
import { useTranslationRef } from '@backstage/frontend-plugin-api'
import { orgTranslationRef } from './translation'

type EnrichedUserProfile = UserEntity['spec']['profile'] & {
  jobTitle?: string
  country?: string
  city?: string
}

/** @public */
export type MemberComponentClassKey = 'card' | 'avatar'

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      card: {
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[0],
        borderRadius: '4px',
        overflow: 'visible',
        position: 'relative',
        margin: theme.spacing(4, 1, 1),
        flex: '1',
        minWidth: '0px'
      },
      avatar: {
        position: 'absolute',
        top: '-2rem'
      },
      bossIcon: {
        position: 'absolute',
        top: '-2rem',
        marginLeft: '4rem',
        fill: '#fd8302'
      }
    }),
  { name: 'PluginOrgMemberComponent' }
)

const MemberComponent = (props: { member: UserEntity }) => {
  const classes = useStyles()
  const {
    metadata: { name: metaName }
  } = props.member
  const profile = props.member.spec.profile as EnrichedUserProfile | undefined
  const displayName = profile?.displayName ?? metaName

  const jobTitle = profile?.jobTitle
  const isBossAlike = jobTitle?.includes('Manager') || jobTitle?.includes('Staff')

  return (
    <Box className={classes.card}>
      <Box
        display="flex"
        flexDirection="column"
        m={3}
        alignItems="center"
        justifyContent="center"
      >
        <Avatar displayName={displayName} picture={profile?.picture} classes={classes} />
        {isBossAlike && <FlareIcon className={classes['bossIcon']} />}
        <Box
          pt={2}
          sx={{
            width: '100%'
          }}
          textAlign="center"
        >
          <Typography variant="h6">
            <EntityRefLink
              data-testid="user-link"
              entityRef={props.member}
              title={displayName}
            />
          </Typography>
          {profile?.email && (
            <Typography variant="body2" gutterBottom>
              <Link to={`mailto:${profile.email}`}>
                <OverflowTooltip text={profile.email} />
              </Link>
            </Typography>
          )}
          {jobTitle && <Typography variant="body1">{jobTitle}</Typography>}
        </Box>
      </Box>
    </Box>
  )
}

/** @public */
export type MembersListCardClassKey = 'root' | 'cardContent' | 'memberList'

const useListStyles = makeStyles(
  theme => ({
    root: {
      height: '100%'
    },
    cardContent: {
      overflow: 'auto'
    },
    memberList: {
      display: 'grid',
      gap: theme.spacing(1.5),
      gridTemplateColumns: `repeat(auto-fit, minmax(auto, ${theme.spacing(44)}px))`,
      alignItems: 'center',
      justifyContent: 'center'
    }
  }),
  { name: 'PluginOrgMembersListCardComponent' }
)

/** @public */
export const MembersListCard = (props: {
  memberDisplayTitle?: string
  pageSize?: number
  showAggregateMembersToggle?: boolean
  relationType?: string
  /** @deprecated Please use `relationAggregation` instead */
  relationsType?: EntityRelationAggregation
  relationAggregation?: EntityRelationAggregation
}) => {
  const { t } = useTranslationRef(orgTranslationRef) as { t: any }
  const {
    memberDisplayTitle = t('membersListCard.title'),
    pageSize = 50,
    showAggregateMembersToggle,
    relationType = 'memberof'
  } = props
  const relationAggregation = props.relationAggregation ?? props.relationsType ?? 'direct'
  const classes = useListStyles()

  const { entity: groupEntity } = useEntity<GroupEntity>()
  const {
    metadata: { name: groupName, namespace: grpNamespace },
    spec: { profile }
  } = groupEntity
  const catalogApi = useApi(catalogApiRef)

  const displayName = profile?.displayName ?? groupName

  const groupNamespace = grpNamespace || DEFAULT_NAMESPACE

  const [page, setPage] = useState(1)
  const pageChange = (_: ChangeEvent<unknown>, pageIndex: number) => {
    setPage(pageIndex)
  }

  const [showAggregateMembers, setShowAggregateMembers] = useState(
    relationAggregation === 'aggregated'
  )

  const { loading: loadingDescendantMembers, value: descendantMembers } =
    useAsync(async () => {
      if (!showAggregateMembers) {
        return [] as UserEntity[]
      }

      return await getAllDesendantMembersForGroupEntity(
        groupEntity,
        catalogApi,
        relationType
      )
    }, [catalogApi, groupEntity, showAggregateMembers])
  const {
    loading,
    error,
    value: directMembers
  } = useAsync(async () => {
    const membersList = await catalogApi.getEntities({
      filter: {
        kind: 'User',
        [`relations.${relationType.toLocaleLowerCase('en-US')}`]: [
          stringifyEntityRef({
            kind: 'group',
            namespace: groupNamespace.toLocaleLowerCase('en-US'),
            name: groupName.toLocaleLowerCase('en-US')
          })
        ]
      }
    })

    return membersList.items as UserEntity[]
  }, [catalogApi, groupEntity])

  const members = removeDuplicateEntitiesFrom(
    [
      ...(directMembers ?? []),
      ...(descendantMembers && showAggregateMembers ? descendantMembers : [])
    ].sort((a, b) => stringifyEntityRef(a).localeCompare(stringifyEntityRef(b)))
  ) as UserEntity[]

  if (loading) {
    return <Progress />
  } else if (error) {
    return <ResponseErrorPanel error={error} />
  }

  const nbPages = Math.ceil((members?.length || 0) / pageSize)
  const paginationLabel =
    nbPages < 2
      ? ''
      : t('membersListCard.paginationLabel', {
          page: String(page),
          nbPages: String(nbPages)
        })

  const pagination = (
    <Pagination
      count={nbPages}
      page={page}
      onChange={pageChange}
      showFirstButton
      showLastButton
    />
  )

  let memberList: JSX.Element
  if (members && members.length > 0) {
    memberList = (
      <Box className={classes.memberList}>
        {members.slice(pageSize * (page - 1), pageSize * page).map(member => (
          <MemberComponent member={member} key={stringifyEntityRef(member)} />
        ))}
      </Box>
    )
  } else {
    memberList = (
      <Box p={2}>
        <Typography>{t('membersListCard.noMembersDescription')}</Typography>
      </Box>
    )
  }

  return (
    <Grid item className={classes.root}>
      <InfoCard
        title={`${memberDisplayTitle} (${members?.length || 0}${paginationLabel})`}
        subheader={t('membersListCard.subtitle', {
          groupName: displayName
        })}
        {...(nbPages <= 1 ? {} : { actions: pagination })}
        className={classes.root}
        cardClassName={classes.cardContent}
      >
        {showAggregateMembersToggle && (
          <>
            {t('membersListCard.aggregateMembersToggle.directMembers')}
            <Switch
              color="primary"
              checked={showAggregateMembers}
              onChange={() => {
                setShowAggregateMembers(!showAggregateMembers)
              }}
              inputProps={{
                'aria-label': t('membersListCard.aggregateMembersToggle.ariaLabel')
              }}
            />
            {t('membersListCard.aggregateMembersToggle.aggregatedMembers')}
          </>
        )}
        {showAggregateMembers && loadingDescendantMembers ? <Progress /> : memberList}
      </InfoCard>
    </Grid>
  )
}
